import { Component, computed, effect, inject, input, model, Signal } from '@angular/core';
import {
  WorkInteraction,
  WorkInteractionRelationTypeEnum,
  WorkInteractionsV2Service,
  WorkTicketStatusChange,
  WorkTicketUserChange,
} from '@work-frontend-monorepo/work-rest-api';
import { TranslateModule } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { ExceptionHandlingService } from '../../services/exception-handling/exception-handling.service';
import { AccordionItemComponent } from '../accordion/accordion-item/accordion-item.component';
import { ButtonComponent } from '../button/button.component';
import { TimelineInteractionItemComponent } from '../timeline-interaction-item/timeline-interaction-item.component';
import {
  TimelineTicketStatusChangeItemComponent
} from '../timeline-ticket-status-change-item/timeline-ticket-status-change-item.component';
import { TimelineTicketUserChangeItemComponent } from '../timeline-ticket-user-change-item/timeline-ticket-user-change-item.component';
import { NgClass } from '@angular/common';

@Component({
  selector: 'work-ui-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],
  standalone: true,
  imports: [
    TranslateModule,
    AccordionItemComponent,
    ButtonComponent,
    TimelineInteractionItemComponent,
    TimelineTicketStatusChangeItemComponent,
    TimelineTicketUserChangeItemComponent,
    NgClass,
  ],
})
export class TimelineComponent {
  interactionsRelationType = input<WorkInteractionRelationTypeEnum>();
  interactionsRelationTypeId = input<number>();
  interactions = model<WorkInteraction[]>([]);
  ticketStatusChanges = input<WorkTicketStatusChange[]>([]);
  ticketUserChanges = input<WorkTicketUserChange[]>([]);
  tableStyle = input<boolean>(false);
  accordionView = input<boolean>(false);
  protected combinedItems: Signal<
    Array<{
      id: number;
      created: string | null;
      interaction?: WorkInteraction;
      ticketStatusChange?: WorkTicketStatusChange;
      ticketUserChange?: WorkTicketUserChange;
    }>
  > = computed(() => {
    const combined = [...this.interactions(), ...this.ticketStatusChanges(), ...this.ticketUserChanges()].map((item) => ({
      ...('interactiontype' in item
        ? { interaction: item as WorkInteraction }
        : 'status' in item
        ? { ticketStatusChange: item as WorkTicketStatusChange }
        : { ticketUserChange: item as WorkTicketUserChange }),
      created: item.created,
      id: item.id,
    }));
    return combined.sort((a, b) => {
      const dateA = a.created ? new Date(a.created) : new Date(0);
      const dateB = b.created ? new Date(b.created) : new Date(0);
      return dateB.getTime() - dateA.getTime();
    });
  });
  private readonly WorkInteractionsV2Service = inject(WorkInteractionsV2Service);
  private readonly exceptionHandlingService = inject(ExceptionHandlingService);
  private loadInteractionsHistoryEffect = effect(async () => {
    const interactionsRelationType = this.interactionsRelationType();
    const interactionsRelationTypeId = this.interactionsRelationTypeId();
    if (interactionsRelationType && interactionsRelationTypeId) {
      await this.loadInteractionsHistory(interactionsRelationType, interactionsRelationTypeId);
    }
  });

  public async loadInteractionsHistory(interactionsRelationType: WorkInteractionRelationTypeEnum, interactionsRelationTypeId: number) {
    try {
      const interactions = await lastValueFrom(
        this.WorkInteractionsV2Service.interactionsV2ControllerFindByRelationType({
          relationType: interactionsRelationType,
          relationTypeId: interactionsRelationTypeId,
        }),
      );
      this.interactions.set(interactions);
    } catch (getInteractionsException) {
      this.exceptionHandlingService.handleExceptionWithAlert('getInteractionsException', getInteractionsException);
    }
  }
}
